import { useRequiredContext } from "@redotech/react-util/context";
import { ConversionFeature } from "@redotech/redo-conversion-settings-common/checkout-optimization-settings";
import CalendarIcon from "@redotech/redo-icons/calendar.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { ScheduleDemoButtonLocation } from "@redotech/redo-merchant-conversion-rpc/schema/analytics/send-schedule-demo-clicked-notification";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { ConversionSuiteStatusesContext } from "../../redo-suite/conversion-cloud";
import { useConversionLockedAction } from "./use-conversion-locked-action";

// Hex values come straight from the Figma node 40:3964 (CRO-1387). The shared
// `bgColor` prop doesn't expose the dark-rail surface color, so an inline
// background is the lightest-weight match.
const CARD_BG = "#292929";
const TITLE_COLOR = "#fafafa";
const BODY_COLOR = "#d6d6d6";

/**
 * Promo card at the bottom of the Conversion sidebar when the team has
 * effective locks and presentation is lock-icon CTA (≥ $3k). Hidden under $3k.
 *
 * Dismissal is component-state only — reappears each session by design.
 */
export const IntentOnlySidebarPromo = memo(function IntentOnlySidebarPromo() {
  const [dismissed, setDismissed] = useState(false);
  const { lockedFeatures, lockPresentation } = useRequiredContext(
    ConversionSuiteStatusesContext,
  );
  const { onLockedClick } = useConversionLockedAction(
    ScheduleDemoButtonLocation.INTENT_SIDEBAR_PROMO,
    ConversionFeature.AB_TESTS,
  );

  const show = lockedFeatures.size > 0 && lockPresentation === "locked-cta";

  if (dismissed || !show) {
    return null;
  }

  return (
    <Flex
      dir="column"
      gap="lg"
      mt="auto"
      mx="md"
      p="xl"
      style={{
        backgroundColor: CARD_BG,
        borderRadius: "var(--border-radius-lg)",
      }}
    >
      <Flex align="flex-start" dir="row" gap="lg" justify="space-between">
        <Text color={TITLE_COLOR} fontSize="xs" fontWeight="semibold">
          Unlock more revenue per visitor
        </Text>
        <button
          aria-label="Dismiss"
          onClick={() => setDismissed(true)}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "var(--redo-spacing-none)",
            lineHeight: 0,
          }}
          type="button"
        >
          <RedoIcon color="tertiary" Icon={XIcon} size={16} />
        </button>
      </Flex>
      <Text
        color={BODY_COLOR}
        fontSize="xs"
        fontWeight="regular"
        style={{ opacity: 0.8 }}
      >
        Upgrade Conversion Cloud to add AI shipping pricing, post-purchase
        upsells, and customer segmentation to capture revenue you&rsquo;re
        leaving on the table.
      </Text>
      <RedoButton
        hierarchy="primary"
        IconLeading={CalendarIcon}
        onClick={() => void onLockedClick()}
        size="sm"
        text="Schedule a call"
        theme="normal"
      />
    </Flex>
  );
});
